/**
 * The page for billing details.
 *
 * Copyright (c) Ircam Amplify 2025-2026
 */

import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import LinearProgress from '@mui/material/LinearProgress'

import { TierType } from '@amplify3/duplo-back-end'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useEffect, useState } from 'react'
import { handleUpdatePayment } from '@/components/MyAccount'
import TabContainer from '@/components/TabContainer'
import './billing.css'
import { InvoiceLink, PlanInvoice, getInvoice } from './Invoice'
import UsageTab from './UsageTab'

import { logger } from '@/config/logger'
import { LogTag } from '@amplify3/duplo-back-end'
import {
  API,
  fetchCompanyConsumption,
  fetchCompanyKpi,
  fetchJobs,
  fetchServicesInfo,
  fetchUser,
} from '@/api'
import FormReturnHandler from '@/components/FormReturnHandler'
import { getUserState, useSelector } from '@/store'
import AccountManagement from './AccountManagement'
import BillingHistory from './BillingHistory'
import PlanOverview from './PlanOverview'
import UpcomingBill from './UpcomingBill'

/**
 * The default number of invoices in the history list.
 */
const DefaultInitialInvoiceListLength = 12

export const Billing = () => {
  const { companyConsumption, companyKpi, user } = useSelector(getUserState)

  const [isLoadingData, setLoadingData] = useState(true)
  const [isCancelling, setIsCancelling] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [planInvoice, setPlanInvoice] = useState<PlanInvoice | undefined>(
    undefined
  )
  const [invoiceList, setInvoiceList] = useState<InvoiceLink[]>([])
  const [lastInvoiceId, setLastInvoiceId] = useState<string>('')
  const [hasMoreInvoicePage, setMoreInvoicePage] = useState<boolean>(false)
  const [apiInvoiceResponse, setApiInvoiceResponse] = useState<any | undefined>(
    undefined
  )

  const confirmCancellation = async () => {
    try {
      setIsCancelling(true)
      setIsProcessing(true)
      await API(await fetchAuthSession()).delete('/subscription')
      setIsCancelling(false)
      setIsProcessing(false)
    } catch (e: any) {
      setErrorMessage(
        'Error cancelling subscription : ' + e.response.data.error
      )
      setIsCancelling(false)
      setIsProcessing
    }
  }

  /**
   * Fetch invoices from the back-end and update the state apiInvoiceResponse.
   * @param previousId The ID of last fetched invoice to get the next ones.
   */
  async function fetchInvoice(previousId: string | null = null) {
    const params: Record<string, any> = {}
    params['limit'] = DefaultInitialInvoiceListLength
    if (previousId) {
      params['last_key'] = previousId
    }

    API(await fetchAuthSession())
      .get('/user/invoices', { params: params })
      .then((response: any) => {
        setApiInvoiceResponse(response)
      })
  }

  /**
   * Get the list of invoices and the consumption data
   * from the responses of the backend.
   */
  function getUserDataFromApiResponse() {
    if (!apiInvoiceResponse) {
      logger.debug(
        LogTag.BillingPage,
        'getInvoices(): apiInvoiceResponse undefined, do nothing.'
      )
      return
    }

    const plan = getInvoice(companyConsumption, companyKpi, apiInvoiceResponse)
    setPlanInvoice(plan)
    logger.debug(LogTag.BillingPage, 'PlanInvoice:', plan)

    const apiInvoiceList = InvoiceLink.fromUserApi(apiInvoiceResponse)
    setInvoiceList((prev) => {
      // Filter the existing invoices to handle concurrent calls to this
      // function and the race conditions.
      const existingIds = new Set((prev ?? []).map((i) => i.id))
      const newInvoices = apiInvoiceList.filter((i) => !existingIds.has(i.id))
      return [...(prev ?? []), ...newInvoices]
    })

    setLastInvoiceId(apiInvoiceResponse.data.last_key)
    setMoreInvoicePage(apiInvoiceResponse.data.has_more)
  }

  useEffect(() => {
    fetchUser()
    fetchCompanyConsumption()
    fetchCompanyKpi()
    fetchJobs({ refreshing: true })
    fetchServicesInfo()
    fetchInvoice()
  }, [])

  // Request data from backend when consumption and KPI are updated
  useEffect(() => {
    if (companyConsumption && companyKpi && apiInvoiceResponse) {
      getUserDataFromApiResponse()
      setLoadingData(false)
    }
  }, [companyConsumption, companyKpi, apiInvoiceResponse])

  // Logs
  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'companyConsumption:', companyConsumption)
  }, [companyConsumption])

  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'companyKpi:', companyKpi)
  }, [companyKpi])

  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'isLoadingData:', isLoadingData)
  }, [isLoadingData])

  useEffect(() => {
    logger.debug(LogTag.BillingPage, 'invoiceList:', invoiceList)
  }, [invoiceList])

  const TabPlanDetails = () => {
    const updatePaymentHandler =
      TierType.PayAsYouGo === planInvoice?.tier ||
      TierType.Subscription === planInvoice?.tier
        ? () => handleUpdatePayment(user)
        : undefined

    return (
      <div className="bill-plan-details">
        <div className="column column-left">
          <PlanOverview
            className="billing-component-container"
            invoice={planInvoice}
          />
          <BillingHistory
            className="billing-component-container"
            invoiceList={invoiceList}
            getInvoices={fetchInvoice}
            hasMoreInvoicePage={hasMoreInvoicePage}
            lastInvoiceId={lastInvoiceId}
          />
        </div>
        {TierType.TopUpCreditAimd != planInvoice?.tier &&
          <div className="column column-right">
            <UpcomingBill
              className="billing-component-container"
              invoice={planInvoice}
            />
            <AccountManagement
              className="billing-component-container"
              cancelPlan={setIsCancelling}
              updatePayment={updatePaymentHandler}
              cancelAccountPlan={async () => setIsCancelling(true)}
            />
          </div>
        }
      </div>
    )
  }

  const TabUsage = () => {
    return (
      <div>
        <UsageTab
          className="billing-component-container"
          invoice={planInvoice}
        />
      </div>
    )
  }

  const LoadingProgress = () => {
    return (
      <div className="billing-loading-container">
        <div className="linear-progress">
          <LinearProgress
            sx={{
              backgroundColor: '#ffeae6',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#f67d69',
              },
            }}
          />{' '}
        </div>
        Loading...
      </div>
    )
  }

  return (
    <div className="billing-page">
      <Dialog
        open={!!isCancelling}
        onClose={() => {
          setIsCancelling(false)
        }}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto w-[50rem] rounded-lg p-20 bg-primary-50">
            <DialogTitle className="text-center text-[22px] mb-4">
              Cancel subscription
            </DialogTitle>
            <Description className="py-4 text-center text-[18px] mb-4">
              Are you sure you wish to cancel your subscription?
            </Description>

            <div className="w-1/2 mx-auto">
              <div className="grid grid-cols-2 gap-2">
                <div className="w-full items-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-light py-2 px-4 my-2"
                    onClick={() => setIsCancelling(false)}
                  >
                    <div className="button button-label">No</div>
                  </button>
                </div>
                <div className="w-full justify-center">
                  <button
                    type="submit"
                    className="block w-full button button-large button-dark py-2 px-4 my-2"
                    onClick={confirmCancellation}
                    disabled={isProcessing}
                  >
                    <div className="button button-label">Yes</div>
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>

      {errorMessage && (
        <FormReturnHandler
          open={!!errorMessage}
          success={false}
          message={errorMessage}
          onClose={() => setErrorMessage('')}
        />
      )}

      <div className="billing-container">
        {isLoadingData ? (
          <LoadingProgress />
        ) : (
          <TabContainer tabList={['Plan Details', 'Usage']}>
            <TabPlanDetails />
            <TabUsage />
          </TabContainer>
        )}
      </div>
    </div>
  )
}
